/*
 * Dialog d'envoi du bilan par email (conservation nulle).
 * Etapes : formulaire -> code personnel -> envoi -> confirmation ou echec.
 * Le PDF est genere puis chiffre AES 256 DANS le navigateur avec le code
 * personnel affiche a l'ecran. Le code ne quitte jamais le navigateur.
 * Les etapes visuelles vivent dans EmailDialogSteps.jsx.
 */

import { useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { sendBilanEmail, trackEvent } from "@/lib/api";
import { generatePersonalCode, savePrenom } from "@/lib/quizStorage";
import { encryptBilanPdf, downloadPdf, bytesToBase64 } from "@/lib/pdfBilan";
import { makePdfBytes } from "@/lib/bilanActions";
import { FormStep, CodeStep, SentStep, ErrorStep, QuotaStep } from "@/components/bilan/EmailDialogSteps";
import { devWarn } from "@/lib/logger";

const isValidEmail = (value) => {
  const email = (value || "").trim();
  return email.includes("@") && Boolean(email.split("@")[1]?.includes("."));
};

/* Validation pure du formulaire : retourne un message d'erreur ou "". */
const validateEmailForm = (form) => {
  if (!isValidEmail(form.email)) {
    return "Merci d'indiquer une adresse email valide.";
  }
  if (!form.serviceConsent) {
    return "Ton accord est nécessaire pour recevoir ton bilan par email.";
  }
  return "";
};

/*
 * Prepare le payload d'envoi : generation locale du PDF en clair (jamais
 * transmis tel quel), chiffrement AES 256 local avec le code personnel,
 * puis assemblage. Le code ne quitte jamais ce navigateur.
 */
const buildEncryptedPayload = async ({ result, settings, form, personalCode, consentTexts }) => {
  const plainBytes = await makePdfBytes(result, settings, form.prenom);
  const encryptedBytes = await encryptBilanPdf(plainBytes, personalCode);
  return {
    prenom: form.prenom.trim(),
    email: form.email.trim(),
    telephone: form.telephone.trim() || null,
    consentService: form.serviceConsent,
    consentMarketing: form.marketingConsent,
    consentPhone: Boolean(form.telephone.trim()) && form.phoneConsent,
    consentVersion: consentTexts.version || "",
    pdfBase64: bytesToBase64(encryptedBytes),
  };
};

/*
 * Classe l'echec d'envoi : quota atteint (pause conviviale avec compte a
 * rebours fourni par le serveur) ou erreur generique (reessai possible).
 */
const resolveSendFailure = (error) => {
  const detail = error?.response?.data?.detail;
  const isQuota =
    error?.response?.status === 429 &&
    detail &&
    typeof detail === "object" &&
    detail.retry_after_seconds;
  if (isQuota) {
    return { kind: "quota", quota: { seconds: detail.retry_after_seconds, reason: detail.reason } };
  }
  return { kind: "error" };
};

/* ----- Hook d'orchestration : etat, validation, chiffrement et envoi ----- */

function useEmailDialog({ result, settings, prenomInitial, onPrenomChange }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form"); // form | code | sending | sent | error | quota
  const [quota, setQuota] = useState(null); // { seconds, reason } si quota atteint
  const [form, setFormState] = useState({
    prenom: prenomInitial || "",
    email: "",
    telephone: "",
    serviceConsent: false,
    marketingConsent: false,
    phoneConsent: false,
  });
  const [formError, setFormError] = useState("");
  const [personalCode, setPersonalCode] = useState("");
  const [copied, setCopied] = useState(false);

  const consentTexts = settings?.consent_texts || {};
  const setForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const resetAndClose = (value) => {
    setOpen(value);
    if (!value) {
      setStep("form");
      setFormError("");
      setCopied(false);
    }
  };

  const continueToCode = () => {
    const error = validateEmailForm(form);
    setFormError(error);
    if (error) return;
    if (form.prenom.trim()) {
      savePrenom(form.prenom.trim());
      onPrenomChange(form.prenom.trim());
    }
    // Code personnel : genere localement, jamais transmis.
    setPersonalCode(generatePersonalCode());
    setStep("code");
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(personalCode);
      setCopied(true);
      toast.success("Code copié", { description: "Garde ton code personnel en lieu sûr." });
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      // Presse papiers indisponible (permissions navigateur). Aucun contenu journalise.
      toast.error("Copie impossible", { description: "Note ton code personnel manuellement." });
    }
  };

  const downloadNow = async () => {
    try {
      trackEvent("pdf_downloaded");
      const bytes = await makePdfBytes(result, settings, form.prenom);
      downloadPdf(bytes);
    } catch (error) {
      devWarn("[cycliq] generation locale du PDF impossible :", error?.name || "erreur inconnue");
      toast.error("Téléchargement impossible", { description: "Réessaie dans un instant." });
    }
  };

  const sendEmail = async () => {
    setStep("sending");
    setFormError("");
    try {
      trackEvent("email_requested");
      // Transmission du PDF DEJA chiffre (voir buildEncryptedPayload).
      const payload = await buildEncryptedPayload({ result, settings, form, personalCode, consentTexts });
      await sendBilanEmail(payload);
      setStep("sent");
    } catch (error) {
      const failure = resolveSendFailure(error);
      if (failure.kind === "quota") {
        setQuota(failure.quota);
        setStep("quota");
        return;
      }
      // Echec reseau ou serveur : l'utilisatrice peut reessayer ou telecharger.
      devWarn("[cycliq] envoi du bilan chiffre impossible :", error?.name || "erreur inconnue");
      setStep("error");
    }
  };

  return {
    open, step, form, setForm, formError, personalCode, copied, consentTexts, quota,
    resetAndClose, continueToCode, copyCode, downloadNow, sendEmail,
  };
}

/* ----- Composant principal : rendu des etapes ----- */

export const EmailDialog = ({ result, settings, prenomInitial, onPrenomChange, triggerLabel }) => {
  const {
    open, step, form, setForm, formError, personalCode, copied, consentTexts, quota,
    resetAndClose, continueToCode, copyCode, downloadNow, sendEmail,
  } = useEmailDialog({ result, settings, prenomInitial, onPrenomChange });

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogTrigger asChild>
        <Button
          data-testid="bilan-email-open-button"
          variant="outline"
          className="min-h-[52px] px-6 rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold text-base active:scale-[0.98] transition-colors duration-200"
        >
          <Mail className="mr-2 w-4 h-4" aria-hidden="true" />
          {triggerLabel || "Recevoir mon bilan par email"}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-[460px] bg-white max-h-[90vh] overflow-y-auto">
        {step === "form" && (
          <FormStep
            form={form}
            setForm={setForm}
            consentTexts={consentTexts}
            formError={formError}
            onContinue={continueToCode}
          />
        )}
        {(step === "code" || step === "sending") && (
          <CodeStep
            personalCode={personalCode}
            sending={step === "sending"}
            copied={copied}
            onCopy={copyCode}
            onDownload={downloadNow}
            onSend={sendEmail}
          />
        )}
        {step === "sent" && (
          <SentStep personalCode={personalCode} copied={copied} onCopy={copyCode} onClose={() => resetAndClose(false)} />
        )}
        {step === "quota" && quota && (
          <QuotaStep
            personalCode={personalCode}
            seconds={quota.seconds}
            reason={quota.reason}
            onRetry={sendEmail}
            onDownload={downloadNow}
          />
        )}
        {step === "error" && (
          <ErrorStep personalCode={personalCode} onRetry={sendEmail} onDownload={downloadNow} />
        )}
      </DialogContent>
    </Dialog>
  );
};
